import React, { useState, useEffect, useMemo } from "react";
import ReactGA from "react-ga";
import PropTypes from "prop-types";
import _ from "lodash";
import axios from "axios";
import { makeStyles } from "@material-ui/core/styles";
import grey from "@material-ui/core/colors/grey";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import TextField from "@material-ui/core/TextField";
import ClearIcon from "@material-ui/icons/Clear";
import IconButton from "@material-ui/core/IconButton";
import MailIcon from "../../../../icons/MailIcon";
import GroupIcon from '../../../../icons/GroupIcon';
import TrashIcon from '../../../../icons/TrashIcon';
import PlaneIcon from '../../../../icons/PlaneIcon';
import TypographyTitle from "../../../../TypographyTitle";
import Button from "../../../../Button";
import TypographyParagraph from '../../../../TypographyParagraph';
import ShareReportTable from './components/ShareReportTable';
import ShareReportActionsDialog from './components/ShareReportActionsDialog';
import ShareReportDialogSubhead from "./components/DialogSubhead";
import ShareAction from '../../../../enums/ShareAction';

ReactGA.initialize("UA-52754256-15");

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    width: 853,
  },
  titleContainer: {
    backgroundColor: "#f6f6f6",
  },
  titleRowTop: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 24,
    lineHeight: "30px",
    fontWeight: 300,
    padding: theme.spacing(1.5, 1, 0, 3),
  },
  peopleIcon: {
    color: theme.palette.primary.main,
    margin: "auto 8px auto 2px",
  },
  titleText: {
    margin: 0,
  },
  closeButton: {
    margin: "0 0 4px auto",
    color: grey[400],
  },
  titleRowBottom: {
    height: 70,
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(0, 3, 3, 3),
  },
  titleRowBottomBlue: {
    height: 70,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 3),
    backgroundColor: theme.palette.primary.main,
  },
  searchInput: {
    marginRight: theme.spacing(2),
  },
  outlinedInputRoot: {
    height: 46,
    fontSize: 15,
    background: "#ffffff",
    borderRadius: 7,
  },
  inputIcon: {
    color: grey[300],
    fontSize: 20,
  },
  notchedOutline: {
    borderRadius: 7,
  },
  outlinedInput: {
    padding: "13.5px 14px 13.5px 6px",
  },
  mailIcon: {
    display: 'flex',
  },
  sendButton: {
    height: 46,
    width: 90,
    borderRadius: 7,
  },
  sendButtonLabel: {
    fontSize: 13,
  },
  cancelButtonLabel: {
    fontSize: 13,
    color: 'white',
    marginTop: theme.spacing(0.25),
  },
  dialogContent: {
    width: '100%',
  },
  filler: {
    flexGrow: 1,
  },
  paragraph: {
    margin: theme.spacing(0, 1.5, 0, 0.25),
    color: '#ffffff',
    fontSize: 16,
  },
  resendIcon: {
    marginLeft: 37,
    cursor: 'pointer',
    color: theme.palette.primary.contrastText,
    fontSize: 18,
  },
  deleteIcon: {
    marginLeft: 37,
    cursor: 'pointer',
    color: theme.palette.primary.contrastText,
    fontSize: 20,
  }
}))

const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const usernameRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@/;
const domainRegex = /@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

function ShareReport(props) {
  const { open, report, onClose } = props;
  const classes = useStyles();
  const [inputEmail, setInputEmail] = useState('');
  const [error, setError] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [actionsDialogOpen, setActionsDialogOpen] = useState('');
  const [dataShared, setDataShared] = useState([]);
  const [dataInherited, setDataInherited] = useState([]);
  const [selectedShared, setSelectedShared] = useState([]);
  const sharedAndInheritedData = _.intersectionBy(dataInherited, dataShared, 'email');
  let emailsReady = [];
  if (selectedShared.length) {
    emailsReady = [...selectedShared];
  } else {
    emailsReady = [inputEmail];
  }

  useEffect(() => fetchShareData(), []);

  function fetchShareData() {
    axios.get(`/api/summary-report-tree-access?id=${report.id}`)
      .then(resp => {
        const shared = [];
        const inherited = [];
        resp.data.forEach(userObj => {
          if (userObj.email) userObj.inherited ? inherited.push(userObj) : shared.push(userObj)
        });
        setDataShared(shared);
        setDataInherited(inherited);
      }).catch(err => {
        console.log(err);
      })
  }

  function handleChange(event) {
    const { name, value } = event.target;
    let emailErrors = '';
    if (!emailRegex.test(value)) {
      const emailErrorsArr = [];
      const username = value.match(usernameRegex);
      const domain = value.match(domainRegex);
      const hasValidUsername = username && username[1] && username[1].length;
      const hasValidDomain = domain && domain[1] && domain[1].length;
      !hasValidUsername && emailErrorsArr.push('a valid username');
      !/@/.test(value) && emailErrorsArr.push("an '@' symbol");
      !hasValidDomain && emailErrorsArr.push("a valid domain (i.e. 'safeandreliablecare.com')");
      if (emailErrorsArr.length === 1) emailErrors = emailErrorsArr.join('');
      if (emailErrorsArr.length === 2) emailErrors = emailErrorsArr.join(' and ');
      if (emailErrorsArr.length > 2) emailErrors = emailErrorsArr.slice(0, -1).join(', ') + ', and ' + emailErrorsArr.slice(-1);
      emailErrors = 'Your email must include ' + emailErrors;
    }
    setInputEmail(value);
    setError(!emailRegex.test(value));
    setErrorText(emailErrors);
  };

  function clearState() {
    setInputEmail('');
    setError(false);
    setErrorText('');
    setSelectedShared([]);
    setActionsDialogOpen('');
  }

  function openResendDialog() {
    setActionsDialogOpen(ShareAction.RESEND);
  }

  function openRemoveAccessDialog() {
    setActionsDialogOpen(ShareAction.REMOVE);
  }

  function closeActionsDialog() {
    setActionsDialogOpen('');
  }

  function closeShareDialog(event, msg = '') {
    event && event.persist();
    closeActionsDialog();
    onClose(msg);
  };

  function handleKeyPress(event) {
    if (event.key === "Enter") {
      handleClickSend();
    }
  }

  function handleClickSend() {
    const isDuplicateInherited = _.find(dataInherited, obj => obj.email === inputEmail);
    const isDuplicateShared = _.find(dataShared, obj => obj.email === inputEmail);
    if (isDuplicateInherited || isDuplicateShared) {
      openResendDialog();
    } else {
      handleSendEmail(null, emailsReady, clearState);
    }
  }

  function handleSendEmail(event, emails, successCallback) {
    emails.forEach(email => {
      fetch('/api/summary-reports/share', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        method: 'POST',
        body: JSON.stringify({
          id: report.id,
          email,
          dataset: report.datasetName
        })
      }).then(resp => {
        fetchShareData();
        successCallback();
        resp.text().then(msg => {
          const sentences = msg.split('.')[1];
          const sendOrResend = sentences.split(' ')[1];
          emails.length === 1 && closeShareDialog(null, msg);
          emails.length > 1 && closeShareDialog(null, `Report shared. ${sendOrResend} email to ${emails.length} recipients.`);
        });
      }).catch(err => {
        console.log(err);
      })
    })
  };

  function handleRemoveAccess(event, emails, successCallback) {
    emails.forEach(email => {
      fetch('/api/summary-reports/remove-access', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        method: 'POST',
        body: JSON.stringify({
          id: report.id,
          email,
        })
      }).then(resp => {
        fetchShareData();
        successCallback();
        resp.text().then(msg => {
          emails.length === 1 && closeShareDialog(null, msg);
          emails.length > 1 && closeShareDialog(null, `Access removed for ${emails.length} users.`);
        });
      }).catch(err => {
        console.log(err);
      })
    })
  }

  function clearAllSelected() {
    setSelectedShared([]);
  }
  const hasDirectAccessUsers = Boolean(dataShared.length)
  const hasInheritedAccessUsers = Boolean(dataInherited.length);
  return (
    <React.Fragment>
      <Dialog
        disableBackdropClick
        disableEscapeKeyDown
        open={open}
        maxWidth="md"
        classes={{ paper: classes.dialogPaper }}
      >
        <div className={classes.titleContainer}>
          <div className={classes.titleRowTop}>
            <GroupIcon className={classes.peopleIcon} />
            <TypographyTitle className={classes.titleText}>Share report with others</TypographyTitle>
            <IconButton className={classes.closeButton} onClick={closeShareDialog}>
              <ClearIcon />
            </IconButton>
          </div>
          {selectedShared.length ?
            (
              <div className={classes.titleRowBottomBlue}>
                <TypographyParagraph className={classes.paragraph}>
                  {selectedShared.length} {selectedShared.length === 1 ? 'user' : 'users'} selected
                </TypographyParagraph>
                <Button onClick={clearAllSelected} classes={{ label: classes.cancelButtonLabel }}>Cancel</Button>
                <div className={classes.filler} />
                <TrashIcon className={classes.deleteIcon} onClick={openRemoveAccessDialog} />
                <PlaneIcon className={classes.resendIcon} onClick={openResendDialog} />
              </div>
            ) :
            (
              <div className={classes.titleRowBottom}>
                <TextField
                  className={classes.searchInput}
                  autoFocus
                  fullWidth
                  variant="outlined"
                  error={error}
                  helperText={errorText}
                  onChange={handleChange}
                  onKeyPress={handleKeyPress}
                  InputProps={{
                    classes: { root: classes.outlinedInputRoot, input: classes.outlinedInput, notchedOutline: classes.notchedOutline },
                    startAdornment: <MailIcon className={classes.inputIcon} />
                  }}
                />
                <Button
                  className={classes.sendButton}
                  onClick={handleClickSend}
                  variant="contained"
                  disabled={error || inputEmail === ''}
                  classes={{ label: classes.sendButtonLabel }}
                >
                  Send
                </Button>
              </div>
            )
          }
        </div>
        <DialogContent>
          <div className={classes.dialogContent}>
            {hasDirectAccessUsers && <ShareReportDialogSubhead
              label="Shared Directly"
              metaLabel="Report shared directly with a member. Member will have access to all reports underneath it in the org hierarchy. You can remove direct access for any member."
            />}

            {useMemo(() => {
              if (hasDirectAccessUsers) return (
                <ShareReportTable
                  type="shared"
                  selected={selectedShared}
                  setSelected={setSelectedShared}
                  data={dataShared}
                  handleSendEmail={handleSendEmail}
                  handleRemoveAccess={handleRemoveAccess}
                  sharedAndInheritedData={sharedAndInheritedData}
                />
              )
            }, [JSON.stringify(selectedShared), JSON.stringify(dataShared)])}
            {hasInheritedAccessUsers && <ShareReportDialogSubhead
              label="Inherited Access"
              metaLabel="Member has access to this report through a higher level report in the org hierarchy. You can only remove access by deleting their direct access to the higher-level report."
            />}

            {useMemo(() => {
              if (hasInheritedAccessUsers) {
                return (
                  <ShareReportTable
                    type="inherited"
                    data={dataInherited}
                    handleSendEmail={handleSendEmail}
                    handleRemoveAccess={handleRemoveAccess}
                    sharedAndInheritedData={sharedAndInheritedData}
                  />
                )
              }
            }, [JSON.stringify(dataInherited)])}
          </div>
        </DialogContent>
      </Dialog>
      <ShareReportActionsDialog
        type={actionsDialogOpen}
        emails={emailsReady}
        sharedAndInherited={sharedAndInheritedData.map(obj => obj.email)}
        handleConfirm={actionsDialogOpen === ShareAction.RESEND ? handleSendEmail : handleRemoveAccess}
        handleCancel={closeActionsDialog}
        successCallback={clearState}
      />
    </React.Fragment>
  );
}

ShareReport.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  report: PropTypes.object.isRequired,
}

export default ShareReport;



// WEBPACK FOOTER //
// ./src/components/SRHHeaders/SummaryReportHeader/components/ShareReport/index.jsx